import { useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { MagnifiableImage } from "@/components/waste/magnifiable-image";
import { TierBadge } from "@/components/waste/tier-badge";
import { RelabelDrawer, type RelabelChoice } from "@/pages/labelling/relabel-drawer";
import { ShiftBar } from "@/pages/labelling/shift-bar";
import { EndShiftDialog, type EndShiftMode } from "@/pages/labelling/end-shift-dialog";
import { CantVerifyDialog } from "@/pages/labelling/cant-verify-dialog";
import {
  isConflict,
  useEndShift,
  useNextItem,
  useShiftState,
  useStartShift,
  useSubmitAction,
  useUndoLast,
  type LabellingActionInput,
  type LabellingItem,
  type QueueCounts,
  type ShiftState,
} from "@/hooks/use-labelling";
import { defaultLocale } from "@/i18n/config";
import { formatCurrency as formatMoney } from "@/lib/format";
import type { CantVerifyReason } from "@/lib/cant-verify-reasons";
import type { BoundingArea } from "@/lib/image-crop";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { WASTE_TYPE_LABELS } from "@/lib/waste-types";

/** Whether the capture a verdict would be about is actually on screen yet. */
type PhotoState = "loading" | "ready" | "failed";

/** When the lease chip starts warning rather than just reporting. */
const LEASE_WARNING_SECONDS = 60;

/**
 * Display only — the card was rendering the stored value, so a labeller read
 * `post_consumer` and `lunch` where the rest of the app says "Post-consumer" and
 * "Lunch". Waste types come from `lib/waste-types`, which is their canonical home;
 * service times have none yet (`transaction-card`, `filter-popover` and
 * `transaction-filters` each keep their own copy), so this mirrors the card that
 * renders the same two fields on the Transactions page rather than adding a fourth
 * definition claiming to be authoritative.
 */
const SERVICE_TIME_LABELS: Record<string, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  unassigned: "Unassigned",
};

const serviceTimeLabel = (key: string | null) =>
  key === null ? "Unassigned" : (SERVICE_TIME_LABELS[key] ?? key);

/** What the toast says once the server has taken each kind of decision. */
const DONE_MESSAGE: Record<LabellingActionInput["action"], string> = {
  correct: "Marked correct",
  relabel: "Relabelled",
  discard: "Discarded",
  cant_verify: "Flagged as unverifiable",
};

const formatLease = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

/**
 * Seconds left on the item lock, or null when nothing is held. Ticks once a second.
 *
 * `lock_expires_at` is server time and this compares it against the browser's clock, so
 * the number is approximate under skew. That is fine for what it is for — making a
 * five-minute deadline visible instead of letting it arrive as a refused verdict. The
 * guarantee lives on the server, and a conflict recovers by claiming the next item.
 */
function useLeaseRemaining(expiresAt: string | null): number | null {
  const [left, setLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!expiresAt) {
      setLeft(null);
      return;
    }
    const tick = () =>
      setLeft(Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000)));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  return left;
}

/**
 * The labeller workstation — one item at a time, three decisions, nothing else.
 *
 * Rendered on a chromeless route outside AppLayout: a labeller sees only this
 * screen, so there is no sidebar, breadcrumb or nav to reach anything from.
 *
 * Visual language from `design/labeller.html`, built with the design system per
 * DESIGN_PROMPT (which wins where the two differ). The mock's Google Fonts
 * `<link>` is deliberately not carried across — POS2-1066 records that host as
 * blocked on customer networks; typography comes from the app's own stack.
 */
export default function LabellerWorkstationPage() {
  const shiftState = useShiftState();
  const shift = shiftState.data?.shift ?? null;
  const startShift = useStartShift();
  const endShift = useEndShift();
  const submitAction = useSubmitAction();
  const undoLast = useUndoLast();

  const item = useNextItem(!!shift);
  const [relabelOpen, setRelabelOpen] = useState(false);
  const [cantVerifyOpen, setCantVerifyOpen] = useState(false);
  // Which way out is being confirmed, or none. One value rather than two booleans:
  // the dialog cannot be open in both modes at once, and this says so (POS2-1093).
  const [confirming, setConfirming] = useState<EndShiftMode | null>(null);

  // A verdict is a statement about a photo, so no verdict is offered until that photo
  // is on screen. On a slow connection the card arrives well before the capture does,
  // and "Correct" is one keystroke — the labeller would be agreeing with a label they
  // have not checked, which is the one thing this whole queue exists to prevent.
  //
  // A missing URL cannot resolve, so it starts failed rather than waiting forever.
  // Eligibility no longer serves imageless events (20260803102500), so this is the
  // belt to that braces.
  const [photo, setPhoto] = useState<PhotoState>("loading");

  // Handling time is measured from when this card appeared, not from when the
  // lock was taken: the gap between them is network and render, not work.
  //
  // Keyed on the lease rather than the event id, because a lapsed lease can serve the
  // *same* event back — it is still top of the queue and nobody else took it. On the
  // event id that re-serve is invisible, so the clock kept running: a real session
  // recorded 2,027 seconds of "handling time" for one Correct. Every fresh claim has a
  // new expiry, and a genuine resume keeps its old one, which is exactly the
  // distinction wanted here.
  const shownAt = useRef<number>(Date.now());
  useEffect(() => {
    shownAt.current = Date.now();
    setRelabelOpen(false);
    setCantVerifyOpen(false);
    setPhoto(item.data?.image_read_url ? "loading" : "failed");
  }, [item.data?.lock_expires_at, item.data?.image_read_url]);

  const current = item.data ?? null;
  const busy = submitAction.isPending || undoLast.isPending || item.isFetching;

  // The lease has always been in the payload and nothing rendered it, so the first a
  // labeller knew of it was a red toast refusing the verdict they had just spent five
  // minutes forming. Now it counts down in the strip, and running out is a transition
  // rather than an error: the item goes back to the queue and the next one arrives.
  const secondsLeft = useLeaseRemaining(current?.lock_expires_at ?? null);
  const refetchItem = item.refetch;
  useEffect(() => {
    if (secondsLeft !== 0) return;
    setRelabelOpen(false);
    setCantVerifyOpen(false);
    toast.warning("Time on that item ran out — it went back to the queue. Loading the next one.");
    void refetchItem();
  }, [secondsLeft, refetchItem]);

  function act(
    action: LabellingActionInput["action"],
    extra?: RelabelChoice | { cant_verify_reason: CantVerifyReason },
  ) {
    if (!current || busy) return;
    submitAction.mutate(
      {
        event_id: current.event_id,
        action,
        handling_time_ms: Date.now() - shownAt.current,
        ...extra,
      },
      {
        onSuccess: () => {
          setRelabelOpen(false);
          setCantVerifyOpen(false);
          toast.success(DONE_MESSAGE[action]);
        },
        // A conflict is an expected transition, not a fault: the hook has already
        // asked for the next item, so this says what happened and that it is handled.
        // Red would tell a labeller they did something wrong, and they did not.
        onError: (error) =>
          isConflict(error)
            ? toast.warning(`${error.message}. Loading the next item.`)
            : toast.error(error.message),
      },
    );
  }

  function undo() {
    if (!shiftState.data?.can_undo || busy) return;
    undoLast.mutate(undefined, {
      onSuccess: () => toast.success("Undone"),
      onError: (error) => toast.error(error.message),
    });
  }

  // C / R / D / U, per the PRD. Disabled while off shift, while a mutation is in
  // flight (so a double-tap cannot submit twice against one lock), and until the
  // capture is on screen — the shortcuts are gated with the buttons, or the keyboard
  // would be a way around the thing the buttons are protecting.
  //
  // `canFlag` is the same minus the photo, and that omission is the point of
  // POS2-1083: Can't verify is the one action whose whole purpose is "I cannot see
  // this", so gating it on the thing that failed is the single combination that
  // leaves a labeller with no move at all. Before it existed, a permanently failing
  // capture could only be escaped by reloading the page, which abandoned the item
  // for the rest of its five-minute lease.
  const canAct = !!shift && !!current && !busy && photo === "ready";
  const canFlag = !!shift && !!current && !busy;
  const dialogOpen = relabelOpen || cantVerifyOpen;
  useHotkeys("c", () => act("correct"), { enabled: canAct && !dialogOpen }, [canAct, dialogOpen]);
  useHotkeys("d", () => act("discard"), { enabled: canAct && !dialogOpen }, [canAct, dialogOpen]);
  // `preventDefault` because this shortcut opens a focused search box: the drawer
  // takes focus while the keypress is still being delivered, so without it the "r"
  // is inserted into the field and seeds the catalogue search with a stray letter.
  useHotkeys(
    "r",
    () => setRelabelOpen(true),
    { enabled: canAct && !dialogOpen, preventDefault: true },
    [canAct, dialogOpen],
  );
  useHotkeys("v", () => setCantVerifyOpen(true), { enabled: canFlag && !dialogOpen }, [
    canFlag,
    dialogOpen,
  ]);
  useHotkeys("u", undo, { enabled: !!shift && !busy }, [shift, busy]);

  return (
    <div className="min-h-screen bg-gray-50/80 antialiased">
      {/* No onStart: off shift the card owns that call to action. Two identical
          primary buttons on one screen was the duplication this removed. */}
      <ShiftBar
        state={shiftState.data ?? null}
        onRequestEnd={() => setConfirming("end")}
        // Off shift there is nothing to end, so the question has one answer and is
        // not worth asking. On shift, signing out closes the hours record — the same
        // thing End shift does, reached by a smaller button.
        onRequestSignOut={() => (shift ? setConfirming("sign-out") : void supabase.auth.signOut())}
      />

      <main className="mx-auto max-w-[1120px] px-6 pb-16 pt-6">
        {!shift ? (
          <OffShift
            state={shiftState.data ?? null}
            onStart={() => startShift.mutate()}
            isStarting={startShift.isPending}
          />
        ) : item.isLoading ? (
          <CardSkeleton />
        ) : current ? (
          <>
            <QueueStrip
              item={current}
              heavyPending={shiftState.data?.queue.heavy_pending ?? 0}
              pending={shiftState.data?.queue.pending ?? 0}
              canUndo={!!shiftState.data?.can_undo && !busy}
              onUndo={undo}
              secondsLeft={secondsLeft}
            />
            <ItemCard
              item={current}
              busy={busy}
              photo={photo}
              onPhoto={setPhoto}
              onCorrect={() => act("correct")}
              onDiscard={() => act("discard")}
              onRelabel={() => setRelabelOpen(true)}
              onCantVerify={() => setCantVerifyOpen(true)}
            />
          </>
        ) : (
          <QueueClear />
        )}
      </main>

      <RelabelDrawer
        eventId={relabelOpen ? (current?.event_id ?? null) : null}
        isSubmitting={submitAction.isPending}
        onClose={() => setRelabelOpen(false)}
        onChoose={(choice) => act("relabel", choice)}
      />

      <CantVerifyDialog
        open={cantVerifyOpen && !!current}
        isSubmitting={submitAction.isPending}
        onClose={() => setCantVerifyOpen(false)}
        onChoose={(reason) => act("cant_verify", { cant_verify_reason: reason })}
      />

      <EndShiftDialog
        shift={confirming ? shift : null}
        mode={confirming ?? "end"}
        isEnding={endShift.isPending}
        onKeepLabelling={() => setConfirming(null)}
        onConfirm={() => {
          const mode = confirming;
          endShift.mutate(undefined, {
            onSuccess: () => {
              setConfirming(null);
              // Signing out only after the shift is closed. The other order leaves the
              // clock running on a labeller who is no longer signed in, which is the
              // bug this fixes, recreated on a worse path.
              if (mode === "sign-out") {
                void supabase.auth.signOut();
                return;
              }
              toast.success("Shift ended — clocked out");
            },
            // The dialog stays up on failure, so a labeller is never signed out of a
            // shift that is still running. Closing the tab is the unclean exit, and
            // that one belongs to the idle sweep.
            onError: (error) => toast.error(error.message),
          });
        }}
      />
    </div>
  );
}

function QueueStrip({
  item,
  heavyPending,
  pending,
  canUndo,
  onUndo,
  secondsLeft,
}: {
  item: LabellingItem;
  heavyPending: number;
  pending: number;
  canUndo: boolean;
  onUndo: () => void;
  secondsLeft: number | null;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
      <div className="flex flex-wrap items-center gap-2.5">
        {item.is_heavy ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700">
            <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} />
            Heavy items · over 10 kg
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
            Recommended · L{item.classification_level ?? "—"}
          </span>
        )}
        <span className="text-xs text-gray-400">
          {item.is_heavy
            ? `${heavyPending} remaining · heavy backlog clears daily before the recommended feed`
            : `${pending} in queue · sorted L4 to L1`}
        </span>
        {secondsLeft !== null && (
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium tabular-nums",
              secondsLeft <= LEASE_WARNING_SECONDS
                ? "bg-amber-50 text-amber-700"
                : "bg-gray-100 text-gray-500",
            )}
            // Announced only once it is nearly out: a chip counting down every second
            // would talk over a screen reader for the whole five minutes.
            role={secondsLeft <= LEASE_WARNING_SECONDS ? "status" : undefined}
          >
            {formatLease(secondsLeft)} left on this item
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onUndo}
        disabled={!canUndo}
        className="text-xs font-medium text-gray-500 underline underline-offset-2 transition-colors duration-150 hover:text-gray-700 disabled:cursor-default disabled:opacity-40"
      >
        Undo last (U)
      </button>
    </div>
  );
}

function ItemCard({
  item,
  busy,
  photo,
  onPhoto,
  onCorrect,
  onRelabel,
  onDiscard,
  onCantVerify,
}: {
  item: LabellingItem;
  busy: boolean;
  photo: PhotoState;
  onPhoto: (state: PhotoState) => void;
  onCorrect: () => void;
  onRelabel: () => void;
  onDiscard: () => void;
  onCantVerify: () => void;
}) {
  const weight = Number(item.item_weight);
  const cost = item.cost_estimate === null ? null : Number(item.cost_estimate);

  return (
    <section className="overflow-hidden rounded-lg border border-black/[0.06] bg-white">
      <div className="flex items-start justify-between gap-4 px-6 pb-4 pt-5">
        <div>
          <div className="text-sm font-semibold text-gray-900">{item.category ?? "Unlabelled"}</div>
          <div className="mt-0.5 text-sm text-gray-500">{item.label ?? "No AI label"}</div>
        </div>
        <div className="text-right">
          <div className="text-base font-semibold tabular-nums text-gray-900">
            {weight.toFixed(2)} kg
          </div>
          <div className="mt-0.5 text-xs tabular-nums text-gray-400">
            {cost === null ? "No cost assigned" : formatCurrency(cost, item.currency)}
          </div>
          <div className="mt-2 flex justify-end">
            <TierBadge level={item.classification_level} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-px border-y border-gray-100 bg-gray-100 sm:grid-cols-2">
        {/* Only the after capture reports its state: it is the photo the verdict is
            about. A missing before image loses the delta signal but not the subject,
            so it does not hold the buttons. */}
        <CaptureImage url={item.before_image_read_url} box={item.bounding_area} tag="Before" />
        <CaptureImage
          url={item.image_read_url}
          box={item.bounding_area}
          tag="After"
          delta={`+${weight.toFixed(2)} kg`}
          onLoaded={() => onPhoto("ready")}
          onFailed={() => onPhoto("failed")}
        />
      </div>

      <dl className="grid grid-cols-1 gap-x-8 px-6 py-4 md:grid-cols-3">
        <MetaRow label="Site" value={item.site_name} />
        <MetaRow label="Service" value={serviceTimeLabel(item.service_time)} />
        <MetaRow label="Type" value={wasteTypeLabelEnglish(item.waste_type ?? "unassigned")} />
        <MetaRow label="Kitchen" value={item.kitchen_name} />
        <MetaRow
          label="Logged"
          value={new Date(item.logged_at).toLocaleString("en-GB", { timeZone: item.timezone })}
        />
        <MetaRow label="Device" value={item.device_serial ?? "—"} mono />
      </dl>

      <div
        className={cn(
          "mx-6 mb-5 flex flex-wrap items-center justify-between gap-4 rounded-lg px-4 py-3",
          photo === "ready" ? "bg-orange-50" : "bg-gray-50",
        )}
      >
        <span
          className={cn("text-sm", photo === "ready" ? "text-orange-700" : "text-gray-500")}
          role={photo === "ready" ? undefined : "status"}
        >
          {photo === "ready"
            ? "Is the AI label right?"
            : photo === "loading"
              ? "Waiting for the capture to load — no verdict until you can see it"
              : "The capture did not load. Use Can't verify to record that and take the next item."}
        </span>
        <div className="flex flex-wrap gap-2.5">
          {/* Never disabled by `photo`, unlike the three beside it. This is the
              action for a capture the labeller cannot see, so the one state it has
              to work in is the one where the others cannot. */}
          <ActionButton onClick={onCantVerify} disabled={busy} tone="neutral">
            Can&apos;t verify <ActionHint>V · cannot judge this</ActionHint>
          </ActionButton>
          <ActionButton onClick={onDiscard} disabled={busy || photo !== "ready"} tone="danger">
            Discard <ActionHint>D · not food / bad capture</ActionHint>
          </ActionButton>
          <ActionButton onClick={onRelabel} disabled={busy || photo !== "ready"} tone="warning">
            Relabel <ActionHint>R · wrong label</ActionHint>
          </ActionButton>
          <ActionButton onClick={onCorrect} disabled={busy || photo !== "ready"} tone="primary">
            Correct <ActionHint>C · label is right</ActionHint>
          </ActionButton>
        </div>
      </div>
    </section>
  );
}

function CaptureImage({
  url,
  box,
  tag,
  delta,
  onLoaded,
  onFailed,
}: {
  url: string | null;
  box: BoundingArea | null;
  tag: string;
  delta?: string;
  onLoaded?: () => void;
  onFailed?: () => void;
}) {
  return (
    <div className="relative bg-white">
      {url ? (
        // Cropped to the device's box (POS2-1105), so the labeller is asked about
        // the frame the classifier was asked about, and magnified on hover
        // (POS2-1106) — this is the surface that most needs a close look at the
        // small and ambiguous items. `onLoaded` still fires from the image itself:
        // it is what releases the verdict buttons and their hotkeys.
        //
        // The badges below are siblings of this frame, not children, so the zoom
        // cannot reach them.
        <MagnifiableImage
          src={url}
          alt={`${tag} capture`}
          box={box}
          className="block h-auto w-full transition-transform duration-100 ease-out"
          onLoad={onLoaded}
          onError={onFailed}
        />
      ) : (
        <div className="flex aspect-[16/10] items-center justify-center text-xs text-gray-400">
          No {tag.toLowerCase()} image
        </div>
      )}
      <span className="absolute left-3 top-3 rounded-md bg-gray-900/70 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-white">
        {tag}
      </span>
      {delta && (
        <span className="absolute bottom-3 right-3 rounded-md bg-blue-600/90 px-2 py-1 text-xs font-semibold tabular-nums text-white">
          {delta}
        </span>
      )}
    </div>
  );
}

function MetaRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3 border-b border-gray-100 py-1.5 text-sm last:border-b-0 md:[&:nth-last-child(-n+3)]:border-b-0">
      <dt className="text-gray-500">{label}</dt>
      <dd
        className={cn(
          "text-right font-medium text-gray-700",
          mono && "font-mono text-xs text-gray-500",
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function ActionButton({
  children,
  onClick,
  disabled,
  tone,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
  tone: "primary" | "warning" | "danger" | "neutral";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex min-w-[112px] flex-col items-center gap-0.5 rounded-md border px-5 py-2.5 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50",
        tone === "primary" && "border-gray-900 bg-gray-900 text-white hover:bg-gray-800",
        tone === "warning" &&
          "border-gray-200 bg-white text-orange-700 hover:border-orange-200 hover:bg-orange-50",
        tone === "danger" &&
          "border-gray-200 bg-white text-red-600 hover:border-red-200 hover:bg-red-50",
        // The quietest of the four: it is not a verdict, and it should not read as
        // the easy way out of an item a labeller could actually judge.
        tone === "neutral" && "border-gray-200 bg-white text-gray-600 hover:bg-gray-50",
      )}
    >
      {children}
    </button>
  );
}

function ActionHint({ children }: { children: React.ReactNode }) {
  return <span className="text-[10px] font-normal opacity-60">{children}</span>;
}

function OffShift({
  state,
  onStart,
  isStarting,
}: {
  state: ShiftState | null;
  onStart: () => void;
  isStarting: boolean;
}) {
  const last = state?.last_shift ?? null;
  const queue = state?.queue ?? null;
  return (
    <section className="rounded-lg border border-black/[0.06] bg-white px-6 py-16 text-center">
      <h2 className="text-sm font-medium text-gray-900">Off shift</h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
        {queue ? queueLine(queue, state?.last_shift?.target_items) : "Loading queue…"}
      </p>
      {last && (
        <p className="mt-3 inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500">
          Last shift · {formatDuration(last.duration_seconds)} · {last.items_labelled} items ·{" "}
          {Math.round(last.items_per_hour)} items/hr
        </p>
      )}
      <div className="mt-6">
        <Button onClick={onStart} disabled={isStarting}>
          {isStarting ? "Starting…" : "Start shift"}
        </Button>
      </div>
    </section>
  );
}

/**
 * The off-shift summary line.
 *
 * The heavy clause is omitted when there is no heavy work: "0 heavy items clear
 * first" states a constraint that does not apply and reads as a fault. The shift
 * target is carried here because off shift the top bar shows no progress bar, so
 * this is the only place it appears.
 */
function queueLine(queue: QueueCounts, target: number | undefined): string {
  const parts = [`${queue.pending} items in queue`];
  if (queue.heavy_pending > 0) {
    parts.push(`${queue.heavy_pending} heavy items clear first`);
  }
  parts.push(`shift target ${target ?? 500}`);
  return parts.join(" · ");
}

function QueueClear() {
  return (
    <section className="rounded-lg border border-black/[0.06] bg-white px-6 py-16 text-center">
      <Check className="mx-auto h-10 w-10 text-gray-300" strokeWidth={1.5} />
      <h2 className="mt-3 text-sm font-medium text-gray-900">Queue clear</h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
        All assigned items are labelled. New events land here as sites log waste — your shift timer
        keeps running until you end it.
      </p>
    </section>
  );
}

function CardSkeleton() {
  return (
    <div className="space-y-4">
      <div className="h-6 w-64 animate-pulse rounded bg-gray-100" />
      <div className="rounded-lg border border-black/[0.06] bg-white p-6">
        <div className="h-10 w-1/3 animate-pulse rounded bg-gray-100" />
        <div className="mt-4 grid grid-cols-2 gap-px">
          <div className="aspect-[16/10] animate-pulse bg-gray-100" />
          <div className="aspect-[16/10] animate-pulse bg-gray-100" />
        </div>
        <div className="mt-4 h-24 animate-pulse rounded bg-gray-100" />
      </div>
    </div>
  );
}

// The workstation is a staff-only, English-only surface (POS2-1133 pinned decision 4), so it
// formats against the default locale rather than the reader's — there is no picker on this screen.
function formatCurrency(value: number, currency: string): string {
  return formatMoney(defaultLocale, value, currency);
}

export function formatDuration(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
}

/** English wording for a waste type. This is a staff-only, English-only surface (pinned decision
 * 4), so it reads the English record directly rather than the reader's catalogue (POS2-1134). */
function wasteTypeLabelEnglish(key: string): string {
  return WASTE_TYPE_LABELS[key] ?? key;
}
